import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import HeadingSection from "../UI/HeadingSection";
import moment from "moment";
import * as r from "../../requests/RMAManagementRequests";
import AlertContext from "../../context/alert/AlertContext";
import { getPlatforms } from "../../requests/platformManagementRequests";
import { getProducts } from "../../requests/productManagementRequests";
import Table from "../UI/Table";
import * as rmaReqs from "../../requests/rmaDetailManagementReqs";
import { useQuery } from "../Hooks";
import { PageWrapper } from "../UI/Grid";
import UserContext from "../../context/user/UserContext";
import Loading from "../UI/Loading";
import Modal from "../UI/Modal";
import { Body, Helper } from "../UI/Text";
import RMADetailForm from "./RMADetailForm";
import styled, { useTheme } from "styled-components";
import Button from "../UI/Button";
import { Flex } from "reflexbox";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const { createRMADetail, getRMADetails } = rmaReqs;

const RMAForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: RMAId } = useParams();
  const { pid, activeUser, isLoading: userLoading } = useContext(UserContext);
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);

  const platforms = useQuery({
    queryID: "platforms",
    request: getPlatforms,
    forcedFilters: {},
    skip: userLoading,
  });

  const result = useQuery({
    queryID: `RMA|${RMAId}`,
    request: r.getRMA,
    queryVariables: { id: RMAId },
    forcedFilters: {},
    skip: !RMAId || platforms?.isLoading || !platforms?.data || userLoading,
  });

  const products = useQuery({
    queryID: "rmaproducts",
    request: getProducts,
    queryVariables: { pid },
    forcedFilters: {},
    attributes: ["ProductNumber", "Name"],
    skip:
      platforms.isLoading ||
      !platforms.data ||
      userLoading ||
      result.isLoading ||
      !result.data,
  });

  const {
    data: rmaDetailsData,
    isLoading: detailsLoading,
    refetchQuery: refetchDetails,
    setData: setDetailData,
  } = useQuery({
    queryID: `rmaDetails_${RMAId}`,
    request: getRMADetails,
    queryVariables: { pid, RMAId },
    forcedFilters: {},
    skip:
      platforms.isLoading ||
      !platforms.data ||
      userLoading ||
      result.isLoading ||
      !result.data ||
      products.isLoading ||
      !products.data,
  });

  const { rows: detailRows = [], count: detailCount } = rmaDetailsData || {};

  const { data: currentRMA, refetchQuery, isLoading, setData } = result || {};
  const { quoteId, erpOrderNum } = currentRMA || {};

  const [show, setShow] = useState(false);
  const [rmaId, setRmaId] = useState("create");

  const disabled = useMemo(() => !!result.data?.rmaCreated, [result.data]);
  const isQuote = useMemo(() => !!quoteId, [quoteId]);

  const productOptions = useMemo(() => {
    const { rows: _products = [] } = products?.data || {};
    return _products.map(({ Name, ProductNumber }) => ({
      value: ProductNumber,
      label: Name,
    }));
  }, [products]);

  const onSubmit = useCallback(async () => {
    try {
      let i = 0;
      for (let detail of detailRows) {
        i += 1;
        const { quantity, productNumber, modelNo, reason } = detail || {};
        if (quantity) {
          if (productNumber && productNumber === "")
            return showFailureAlert(
              `Please set a Product Number for the RMA Detail #${i}`
            );
          if (modelNo && modelNo === "")
            return showFailureAlert(
              `Please set a Model Number for the RMA Detail #${i}`
            );
          if (reason && reason === "")
            return showFailureAlert(
              `Please enter a Reason for the RMA Detail #${i}`
            );
        }
      }

      const { error } = await r.submitRMA({
        id: RMAId,
        input: {
          firstName: activeUser?.firstName,
          lastName: activeUser?.lastName,
        },
      });
      if (error) return showFailureAlert(error);

      showSuccessAlert(`Successfully created RMA: ${RMAId}`);

      refetchQuery("RMAs");
      refetchDetails();
    } catch (error) {
      console.error(error);
      showFailureAlert("Failed to create RMA.");
    }
  }, [
    RMAId,
    detailRows,
    showSuccessAlert,
    activeUser,
    refetchQuery,
    refetchDetails,
    showFailureAlert,
  ]);

  const onDetailDelete = useCallback(
    async (id) => {
      const { rows } = rmaDetailsData;
      const newRows = [...rows];
      const index = _.findIndex(newRows, (row) => row.id === id);
      if (disabled) {
        showFailureAlert(
          `Cannot delete Detail Line ${
            index + 1
          }, RMA has already been submitted.`
        );
        return;
      }

      try {
        _.remove(newRows, (row) => row.id === id);
        setDetailData({ rows: newRows, count: detailCount - 1 });
        await rmaReqs.deleteRMADetail({ id });
        showSuccessAlert(`Successfully deleted Detail Line ${index + 1}.`);
        setRmaId(null);
        setShow(false);
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed to delete Detail Line.");
        setRmaId(null);
        setShow(false);
      }
    },
    [
      disabled,
      detailCount,
      rmaDetailsData,
      setDetailData,
      showFailureAlert,
      showSuccessAlert,
    ]
  );

  const onCreateDetail = useCallback(async () => {
    try {
      const result = await createRMADetail({
        input: { productNumber: "", quantity: 1, RMAId, pid },
      });

      const lineNum = detailCount + 1;
      console.log({ result });

      const rows = [...detailRows, { ...result }];

      setDetailData({
        rows,
        count: lineNum,
      });

      if (result?.id) {
        showSuccessAlert(`Successfully created Detail Line ${lineNum}`);
        setRmaId(result.id);
        setShow(true);
      }
    } catch (error) {
      console.error(error);
      showFailureAlert("Failed to create RMA Detail");
    }
  }, [
    RMAId,
    pid,
    detailCount,
    detailRows,
    setDetailData,
    showSuccessAlert,
    showFailureAlert,
  ]);

  const gridData = useMemo(() => {
    let data = [];
    for (let detail of detailRows) {
      const { reason, productNumber, quantity, id, modelNo, timeInService } =
        detail;
      data.push({
        title: productNumber,
        subtitle: reason,
        elements: [
          <Helper>{`Quantity: ${quantity}`}</Helper>,
          <Helper>{`Model #: ${modelNo}`}</Helper>,
          <Helper>{`Time in service: ${timeInService}`}</Helper>,
        ],
        id,
      });
    }
    return data;
  }, [detailRows]);

  const onDetailClick = useCallback(({ original }) => {
    const { id } = original;
    setRmaId(id);
    setShow(true);
  }, []);

  const renderedOptions = useMemo(
    () =>
      productOptions.map(({ label, value }, i) => (
        <option key={`${value}_${i}`} value={value}>
          {label}
        </option>
      )),
    [productOptions]
  );

  const disableSubmit = useMemo(() => {
    let disable = false;
    if (detailRows?.length < 1) disable = true;
    let numWithQuantity = 0;
    for (let detail of detailRows) {
      const { quantity, productNumber, reason, modelNo } = detail || {};
      if (quantity) {
        numWithQuantity += 1;
        if (!productNumber || !reason || !modelNo) disable = true;
      } else if (numWithQuantity === 0) {
        disable = true;
      }
    }
    return disable;
  }, [detailRows]);

  const subtitle = useMemo(() => {
    const { requestDate, status, rmaCreated } = currentRMA || {};
    return !requestDate
      ? null
      : `Request Date: ${moment(requestDate).format("MM/DD/YYYY")}, Status: ${
          status || rmaCreated ? "Submitted" : "Not Submitted"
        }`;
  }, [currentRMA]);

  const detailColumns = useMemo(() => {
    const ret = [
      {
        id: "productNumber",
        header: "Part #",
        size: { md: 2, sm: 2, xs: 2 },
        data: ({ productNumber }) => <Body center>{productNumber}</Body>,
      },
      {
        id: "modelNo",
        header: "Model #",
        size: { lg: 2, md: 2, sm: 0, xs: 0 },
        data: ({ modelNo }) => <Body center>{modelNo}</Body>,
      },
    ];

    ret.push(
      {
        id: "reason",
        header: "Reason",
        size: { lg: 0, md: 0, sm: 0, xs: 0 },
        data: ({ reason }) => <Body>{reason}</Body>,
      },
      {
        id: "quantity",
        header: "Quantity",
        size: { lg: 1, md: 1, sm: 2, xs: 2 },
        data: ({ quantity }) => <Body center>{quantity}</Body>,
      }
    );

    if (isQuote) {
      ret.push({
        id: "orderedQuantity",
        header: "Ordered Quantity",
        size: { md: 2, sm: 2, xs: 2 },
        data: ({ orderedQuantity }) => <Body center>{orderedQuantity}</Body>,
      });
    }

    /// remove returned items from future rmas
    if (!disabled)
      ret.push({
        display: (props) => {
          const { quantity, productNumber, reason, modelNo } =
            props?.row?.original || {};
          const attentionRequired =
            quantity && (!productNumber || !reason || !modelNo);
          return (
            <ButtonsWrapper id="">
              <Flex alignItems="flex-start">
                {attentionRequired ? <Body color={theme.danger}>*</Body> : null}
                <Button
                  size="small"
                  reverse
                  color={theme.secondary}
                  iconName="faPencil"
                  onClick={() => onDetailClick(props.row)}
                />
              </Flex>
              {/* <Button
                size="small"
                color={theme.danger}
                reverse
                iconName="faTrashCan"
                onClick={() => onDetailDelete(props.row.original?.id)}
              /> */}
            </ButtonsWrapper>
          );
        },
        header: "",
        sort: false,
        size: { lg: 2, md: 2, sm: 2, xs: 2 },
      });
    return ret;
  }, [
    disabled,
    isQuote,
    onDetailClick,
    // onDetailDelete,
    theme.danger,
    theme.secondary,
  ]);

  const handleUpdateRMA = useCallback(
    async (e) => {
      e.preventDefault();

      const value = e.target.value;
      const name = e.target.name;
      if (currentRMA[name] === value) return;
      setData({ ...currentRMA, [name]: value });

      await r.updateRMA({ id: RMAId, input: { [name]: value } });
    },
    [RMAId, currentRMA, setData]
  );

  return isLoading || products.isLoading || !products.data || !currentRMA ? (
    <Loading />
  ) : (
    <>
      <Modal show={show} setShow={setShow}>
        <RMADetailForm
          productOptions={renderedOptions}
          refetchDetails={refetchDetails}
          rmaId={rmaId}
          setRmaId={setRmaId}
          show={show}
          setShow={setShow}
          disabled={disabled}
          setDetailData={setDetailData}
          count={detailCount}
          rows={detailRows}
          isQuote={isQuote}
        />
      </Modal>
      <PageWrapper>
        <HeadingSection
          my="1rem"
          hideCreate={disabled}
          createLink={() => onSubmit()}
          createButtonIcon="faPersonWalkingArrowLoopLeft"
          createButtonText="Submit"
          createButtonColor={theme.success}
          createButtonDisabled={disableSubmit}
          subtitle={subtitle}
          backButtonLink={() => navigate(`/RMAs/${pid}`)}
          title={`${!disabled ? "Create" : "View"} RMA${
            isQuote ? ` from order #${(erpOrderNum || "").trim()}` : ""
          }`}
        />

        <Table
          columns={detailColumns}
          hideBack
          hideToggle
          hideProperties
          hideCreate={disabled}
          title="Detail Lines"
          hideSearch
          isLoading={detailsLoading}
          totalCount={detailCount}
          createLink={() => onCreateDetail()}
          createButtonText="Create RMA Detail"
          data={detailRows}
          gridData={gridData}
        />
      </PageWrapper>
    </>
  );
};

export default RMAForm;
